import { FC } from "react";
import LogoutOutlinedIcon from "@mui/icons-material/LogoutOutlined";

import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";

import privacyDoc from "../../documents/privacyDoc.pdf";
import SolarGikDropdown, { MenuItem } from "../../SolarGikLib/fields/Dropdown";
import { GENERAL_APP_CONFIG } from "../app/configuration/AppConfigModels";
import { useLogout } from "../../common/Hooks/useLogout";
import { resetSiteSpecificData } from "../app/store/GlobalActions";
import { MultiSitePage, SitePage } from "../../pages/PageNames";
import { RootState } from "../app/Store";
import { selectSiteId } from "../sites/SiteStore";
import { isSitePageVisible } from "../navigation/NavigationUtils";
import { useSomaAuth } from "../auth/SomaAuthContext";
import { UserAccessType } from "../user/Models";
import classes from "./UserOptionDropdownMenu.module.scss";

interface IUserOptionDropdownMenuProps {
  icon: FC<React.SVGProps<SVGSVGElement>>;
  addSingleSiteMenuItems: boolean;
}

const UserOptionDropdownMenu: FC<IUserOptionDropdownMenuProps> = ({ icon, addSingleSiteMenuItems }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const Logout = useLogout();
  const { currentAccount, currentLocalUser, isLocalAuthEnabled, localUsers, signInToLocalAuth } =
    useSomaAuth();
  const handleLogoutFunc = () => {
    Logout();
  };
  const siteId = useSelector(selectSiteId);
  const currentSiteMetadata = useSelector((state: RootState) => state.multiSitesMetadata.sites[siteId]);
  const siteFeatureFlags = useSelector(
    (state: RootState) => state.featureFlags.sitesFeatureFlags[siteId]
  );
  const userType = useSelector((state: RootState) => state.user.userType);
  const userName = useSelector((state: RootState) => state.user.userName);
  const displayName =
    userName.trim() ||
    currentLocalUser?.name ||
    currentAccount?.name ||
    currentAccount?.username ||
    "User";
  const accountEmailClaims = currentAccount?.idTokenClaims?.emails;
  const email = [
    currentLocalUser?.email,
    currentAccount?.idTokenClaims?.email,
    ...(Array.isArray(accountEmailClaims) ? accountEmailClaims : []),
    currentAccount?.username,
  ].find((value): value is string => typeof value === "string" && value.includes("@"));
  const initials = displayName
    .split(/\s+/)
    .filter(Boolean)
    .map((part) => part[0])
    .filter((_, index, parts) => index === 0 || index === parts.length - 1)
    .join("")
    .toUpperCase();
  const currentSiteProducts = useSelector(
    (state: RootState) => state.purchasedProducts?.productsBySite[siteId]
  );

  const isSettingsPageVisible = isSitePageVisible(SitePage.Settings, {
    userAccessType: userType,
    siteFeatureFlags: siteFeatureFlags,
    siteMetadata: currentSiteMetadata,
    siteProducts: currentSiteProducts,
  });

  const menuItems: MenuItem[] = [
    {
      label: "Alert notifications",
      onClick: () => navigate(`/${MultiSitePage.AlertNotifications}`),
    },
    ...(addSingleSiteMenuItems && isSettingsPageVisible
      ? [
          {
            label: "Settings",
            onClick: () => {
              navigate(`/${encodeURI(currentSiteMetadata.siteCode)}/${SitePage.Settings}`);
            },
          },
        ]
      : []),
    {
      label: "Privacy Policy",
      onClick: () => window.open(privacyDoc, "_blank"),
    },
    ...(isLocalAuthEnabled
      ? localUsers.map((user) => ({
          label: user.id === currentLocalUser?.id ? `${user.name} (current)` : `Switch to ${user.name}`,
          onClick: () => {
            if (user.id === currentLocalUser?.id) {
              return;
            }

            dispatch(resetSiteSpecificData());
            signInToLocalAuth(user.id);
            navigate(`/${MultiSitePage.Multisite}`, { replace: true });
          },
          disabled: user.id === currentLocalUser?.id,
        }))
      : []),
    {
      label: (
        <span className={classes.logout}>
          <LogoutOutlinedIcon sx={{ fontSize: 18 }} />
          Logout
        </span>
      ),
      onClick: handleLogoutFunc,
    },

    {
      label: "Soma " + GENERAL_APP_CONFIG.portalVersion,

      onClick: () => {},
      disabled: true,
    },
  ];
  return (
    <SolarGikDropdown
      icon={icon}
      buttonTooltip="User profile"
      horizontal="right"
      menuHeader={
        <div className={classes.profile}>
          <span className={classes.avatar} aria-hidden="true">
            {initials}
          </span>
          <div className={classes.identity}>
            <span className={classes.name}>{displayName}</span>
            {email && <span className={classes.email}>{email}</span>}
            <span className={classes.role}>{UserAccessType[userType] ?? "Unknown role"}</span>
          </div>
        </div>
      }
      menuItems={menuItems}
      buttonCustomUi={{
        "&.MuiIconButton-root:hover": {
          backgroundColor: "var(--header-and-nav-hover-color)",
          borderRadius: "48px",
        },
        "@media screen and (max-width: 1600px)": {
          padding: "3px",
        },
      }}
      menuCustomUi={{
        "& .MuiPaper-root": {
          borderRadius: "16px",
          minWidth: "260px",
          maxWidth: "min(340px, calc(100vw - 32px))",
          border: "var(--card-border)",
          boxShadow: "0 8px 32px rgba(31, 49, 113, 0.14)",
        },
        "& .MuiMenu-list": {
          padding: "0 0 6px",
        },
      }}
      menuItemCustomUi={{
        "&.MuiMenuItem-root": {
          margin: "2px 8px",
          padding: "10px 12px",
          borderRadius: "8px",
          fontSize: "13px",
          whiteSpace: "normal",
          "&:hover, &.Mui-focusVisible": {
            backgroundColor: "var(--button-hover-color)",
          },
          [`&:has(.${classes.logout})`]: {
            marginTop: "10px",
            border: "1px solid #fecdca",
            backgroundColor: "#fff5f5",
            color: "#b42318",
            "&:hover, &.Mui-focusVisible": {
              backgroundColor: "#fee4e2",
            },
          },
          "&:last-child": {
            marginTop: "6px",
            borderTop: "var(--card-border)",
            borderRadius: 0,
            fontSize: "11px",
          },
        },
      }}
    ></SolarGikDropdown>
  );
};
export default UserOptionDropdownMenu;
